import { useState, useEffect } from 'react';
import { LogOut, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { useProfileManagement } from '../hooks/useProfileManagement';
import { LoginForm } from '../components/admin/LoginForm';
import { ProfileForm } from '../components/admin/ProfileForm';
import { ProfileList } from '../components/admin/ProfileList';

const Admin = () => {
  const { isAuthenticated, isCheckingAuth, isLoading: authLoading, checkAuth, login, logout } = useAuth();
  const { profiles, isLoading: profilesLoading, loadProfiles, createProfile, updateProfile, deleteProfile, toggleActive } = useProfileManagement();
  
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    sesso: '',
    eta: '',
    fasciaEta: '',
    bio: '',
    attivo: true
  });

  // Age range constants
  const AGE_RANGES = {
    YOUNG: { min: 25, max: 35, label: '25-35' },
    MIDDLE: { min: 36, max: 46, label: '36-46' },
    MATURE: { min: 47, max: 57, label: '47-57' },
    SENIOR: { min: 58, max: 68, label: '58-68' },
    ELDERLY: { min: 69, max: 80, label: '69-80' }
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      loadProfiles();
    }
  }, [isAuthenticated, loadProfiles]);

  const determineFasciaEta = (eta) => {
    const age = parseInt(eta);
    for (const range of Object.values(AGE_RANGES)) {
      if (age >= range.min && age <= range.max) {
        return range.label;
      }
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'eta') {
        updated.fasciaEta = determineFasciaEta(value);
      }
      return updated;
    });
  };

  const handleSessoChange = (value) => {
    setFormData(prev => ({ ...prev, sesso: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      eta: parseInt(formData.eta)
    };

    const success = isEditing
      ? await updateProfile(editingProfile.id, submitData)
      : await createProfile(submitData);

    if (success) {
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      sesso: '',
      eta: '',
      fasciaEta: '',
      bio: '',
      attivo: true
    });
    setIsEditing(false);
    setEditingProfile(null);
    setShowForm(false);
  };

  const handleEdit = (profile) => {
    setFormData({
      nome: profile.nome,
      sesso: profile.sesso,
      eta: String(profile.eta),
      fasciaEta: profile.fasciaEta,
      bio: profile.bio,
      attivo: profile.attivo
    });
    setEditingProfile(profile);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleLogin = async (email, password) => {
    await login(email, password);
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} isLoading={authLoading} />;
  }

  return (
    <div className="min-h-screen bg-warm-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-burgundy">
            Area Amministrazione
          </h1>
          <Button
            onClick={logout}
            variant="outline"
            className="flex items-center gap-2"
          >
            <LogOut size={18} />
            Logout
          </Button>
        </div>

        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="btn-primary mb-6"
          >
            <Plus size={18} className="mr-2" />
            Nuovo Profilo
          </Button>
        )}

        {showForm && (
          <ProfileForm
            formData={formData}
            isEditing={isEditing}
            isLoading={profilesLoading}
            onSubmit={handleSubmit}
            onChange={handleChange}
            onSessoChange={handleSessoChange}
            onCancel={resetForm}
          />
        )}

        <ProfileList
          profiles={profiles}
          onEdit={handleEdit}
          onDelete={deleteProfile}
          onToggleActive={toggleActive}
        />
      </div>
    </div>
  );
};

export default Admin;
